import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaRegCalendarAlt } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import SelecteurPieces, { verifierPieces } from './SelecteurPieces';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';

/**
 * Dépôt d'une demande de congé par son titulaire (contrat C9).
 *
 * La liste des types vient d'Odoo (C10) et n'est jamais complétée ici : seuls
 * les types réellement transmissibles à la Paie y figurent (Yros/Kavero#323).
 * Si elle est vide, l'écran le dit — c'est un défaut de paramétrage côté Paie,
 * pas une panne, et un formulaire sans choix n'aide personne.
 *
 * Les refus métier (mois de paie verrouillé, chevauchement) sont affichés mot
 * pour mot : ils viennent d'Odoo, le front n'en reformule aucun.
 *
 * Assemblage repris de `pages/Paie/PayrollSync.js` (carte blanche, champs et
 * bouton d'action), resserré pour le mobile : la coquille employé se conçoit à
 * 375px, d'où la grille qui ne se dédouble qu'à partir de `sm`.
 */

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

function DemandeConge({ onDepot }) {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [typeId, setTypeId] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [pieces, setPieces] = useState([]);

  const chargerTypes = useCallback(async () => {
    setChargement(true);
    try {
      const res = await apiService.getElement('/rh/types-absence');
      setTypes(res?.data?.types || []);
    } catch (e) {
      signalerErreur(e, 'DemandeConge.chargerTypes');
      setTypes([]);
      setErreur(t('demandeConge.erreurGenerique'));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    chargerTypes();
  }, [chargerTypes]);

  const soumettre = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);

    if (!typeId || !dateDebut || !dateFin) {
      setErreur(t('demandeConge.champsManquants'));
      return;
    }

    const refusPieces = verifierPieces(pieces, t);
    if (refusPieces) {
      setErreur(refusPieces);
      return;
    }

    setEnvoi(true);
    try {
      const demande = {
        type: 'conge',
        contenu: {
          type_conge_id: Number(typeId),
          date_debut: dateDebut,
          date_fin: dateFin,
        },
      };
      // Sans pièce, l'appel est celui d'avant : un seul argument.
      const reponse = pieces.length
        ? await apiService.creerDemandeRh(demande, pieces)
        : await apiService.creerDemandeRh(demande);
      if (reponse && reponse.ok) {
        setSucces(t('demandeConge.succes'));
        setDateDebut('');
        setDateFin('');
        setPieces([]);
        // Le parent recharge « Mes demandes » : la demande qu'on vient de
        // déposer doit apparaître, sinon le message de succès est la seule
        // trace de ce qui s'est passé.
        if (onDepot) onDepot();
      } else {
        setErreur(reponse?.message || t('demandeConge.erreurGenerique'));
      }
    } catch (e) {
      // Seul le message métier de la réponse est affiché : le corps d'une
      // panne (502) porte un `error` technique, pas un message pour l'employé.
      setErreur(e?.response?.data?.message || t('demandeConge.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) {
    return <LoadingBarWithStyles data-testid="demande-conge-loading" />;
  }

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100 space-y-4">
      <div className="flex items-center gap-2">
        <FaRegCalendarAlt className="text-[--color-primary]" />
        <h2 className="text-base sm:text-lg font-semibold">{t('demandeConge.titre')}</h2>
      </div>

      {types.length === 0 ? (
        <p data-testid="demande-vide" className="text-sm text-gray-500">
          {t('demandeConge.aucunType')}
        </p>
      ) : (
        <form data-testid="demande-formulaire" onSubmit={soumettre} className="space-y-4">
          <label className="block text-sm font-medium">
            {t('demandeConge.type')}
            <select
              data-testid="demande-type"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">{t('demandeConge.choisir')}</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.libelle}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block text-sm font-medium">
              {t('demandeConge.du')}
              <input
                type="date"
                data-testid="demande-date-debut"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                className={CLASSE_CHAMP}
              />
            </label>
            <label className="block text-sm font-medium">
              {t('demandeConge.au')}
              <input
                type="date"
                data-testid="demande-date-fin"
                min={dateDebut || undefined}
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                className={CLASSE_CHAMP}
              />
            </label>
          </div>

          <SelecteurPieces idPrefix="demande" fichiers={pieces} onChange={setPieces} />

          {erreur && (
            <p
              data-testid="demande-erreur"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {erreur}
            </p>
          )}
          {succes && (
            <p
              data-testid="demande-succes"
              className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
            >
              {succes}
            </p>
          )}

          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              data-testid="demande-soumettre"
              disabled={envoi}
              className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FaPaperPlane size={14} />
              {envoi ? t('demandeConge.envoi') : t('demandeConge.envoyer')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default DemandeConge;
